import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

import type { SessionStatus } from './session'

/**
 * Navigation rules, kept pure so they can be tested without a router.
 * Hiding a route is not protection: the backend checks every endpoint.
 */

export interface GuardSession {
  status: SessionStatus
  can: (permission: string) => boolean
}

type Target = Pick<RouteLocationNormalized, 'name' | 'meta' | 'fullPath' | 'query'>

/** Where to land when the home screen is not allowed, in order of preference. */
export const LANDING_ROUTES: readonly { name: string; permission: string }[] = [
  { name: 'viewer', permission: 'maps:map:read' },
  { name: 'admin-plans', permission: 'maps:plan:read' },
  { name: 'admin-properties', permission: 'maps:property:read' },
  { name: 'admin-users', permission: 'maps:user:read' },
  { name: 'admin-roles', permission: 'maps:role:read' },
  { name: 'admin-audit', permission: 'maps:audit:read' },
]

/** Only in-app paths are honoured as a post-login destination (no open redirect). */
export function safeRedirect(value: unknown): string | null {
  const raw = Array.isArray(value) ? value[0] : value
  return typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') ? raw : null
}

export function decideNavigation(to: Target, session: GuardSession): true | RouteLocationRaw {
  if (to.name === 'not-found') return true

  if (to.name === 'login') {
    if (session.status === 'denied') return { name: 'access-request' }
    if (session.status === 'ready') return safeRedirect(to.query.redirect) ?? { name: 'viewer' }
    return true
  }

  if (to.name === 'access-request') {
    if (session.status === 'denied') return true
    if (session.status === 'ready') return { name: 'viewer' }
    return { name: 'login' }
  }

  if (session.status === 'denied') return { name: 'access-request' }

  if (session.status !== 'ready') {
    const redirect = to.fullPath !== '/' ? { redirect: to.fullPath } : undefined
    return { name: 'login', query: redirect }
  }

  const permission = to.meta.permission
  if (permission && !session.can(permission)) {
    // Home without map access: send them to the first screen they can use.
    if (to.name === 'viewer') {
      const landing = LANDING_ROUTES.find((route) => session.can(route.permission))
      if (landing) return { name: landing.name }
    }
    return { name: 'forbidden' }
  }

  return true
}
